import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoIcon from "@mui/icons-material/Photo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import {
  useActionEvidence,
  useDeleteActionFileEntry,
  FILE_UPLOAD_TYPE,
  ActionFileEntry,
} from "model";
import { downloadFileHelper } from "features/fileManagement/downloadFileHelper";
import { useAppStore } from "stores";
import { useState } from "react";
import EvidenceViewerDialog from "./EvidenceViewerDialog";

interface Props {
  actionSubmissionGuid: string;
}

export default function ActionEvidenceList({ actionSubmissionGuid }: Props) {
  const { data: evidence = [], isLoading } =
    useActionEvidence(actionSubmissionGuid);
  const deleteActionFileEntry = useDeleteActionFileEntry();
  const { notify } = useNotification();
  const user = useAppStore((state) => state.user);
  const [previewItem, setPreviewItem] = useState<ActionFileEntry | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (evidence.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#71717a", fontSize: "14px" }}>
        No evidence uploaded yet.
      </Typography>
    );
  }

  const handleDelete = async (guid: string) => {
    try {
      await deleteActionFileEntry.mutateAsync({
        guid,
        actionSubmissionGuid,
      });
      notify("Evidence removed");
    } catch {
      notify("Failed to remove evidence", "error");
    }
  };

  return (
    <>
      <List disablePadding>
        {evidence.map((item) => {
          const isPhoto =
            item.fileUploadType ===
            FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf();
          const isOwner = item.uploaderGuid === user?.guid;

          return (
            <ListItem
              key={item.guid}
              sx={{
                border: "1px solid #e4e4e7",
                borderRadius: "6px",
                mb: 1,
                pl: 1.5,
                pr: isOwner ? "116px" : "80px",
                py: 0.75,
                cursor: "pointer",
                "& .MuiListItemSecondaryAction-root": {
                  right: 8,
                },
              }}
              onClick={() => setPreviewItem(item)}
              secondaryAction={
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewItem(item);
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await downloadFileHelper(item.fileEntryGuid, notify);
                    }}
                  >
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                  {isOwner && (
                    <IconButton
                      size="small"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await handleDelete(item.guid);
                      }}
                      disabled={deleteActionFileEntry.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                {isPhoto ? (
                  <PhotoIcon sx={{ color: "#16a34a" }} />
                ) : (
                  <InsertDriveFileIcon sx={{ color: "#3b82f6" }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={item.fileName || "Unnamed file"}
                primaryTypographyProps={{ fontSize: "14px", noWrap: true }}
                secondary={
                  item.creationDate
                    ? new Date(item.creationDate).toLocaleDateString()
                    : undefined
                }
                secondaryTypographyProps={{ fontSize: "12px" }}
              />
            </ListItem>
          );
        })}
      </List>
      <EvidenceViewerDialog
        open={!!previewItem}
        fileEntryGuid={previewItem?.fileEntryGuid ?? ""}
        fileName={previewItem?.fileName ?? ""}
        onClose={() => setPreviewItem(null)}
      />
    </>
  );
}
